import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, MagnifyingGlass, ArrowsClockwise, DownloadSimple, Image as ImageIcon, Share, Check, Copy, ArrowSquareOut, WhatsappLogo, Receipt, ArrowRight, ChatCircleText, Sparkle, CurrencyDollar, ArrowsLeftRight, FunnelSimple, X, FilePdf, Package, Clock, FirstAidKit, Storefront } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SuggestPhotoDialog from "@/components/SuggestPhotoDialog";
import { ShareLotDialog } from "@/components/ShareLotDialog";
import ProductHistoryDialog from "@/components/ProductHistoryDialog";
import { toast } from "sonner";
import ProductForm from "@/components/ProductForm";
import MovementForm from "@/components/MovementForm";
import PhotoLightbox from "@/components/PhotoLightbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { AlendraSeoModal, AlendraPricingModal } from "@/components/AlendraAI";
import { useAuth, hasRole } from "@/context/AuthContext";
import { useSpecSchemas } from "@/lib/specSchemas";
import BulkSpecAIDialog from "@/components/BulkSpecAIDialog";
import ProductActionsMenu from "@/components/products/ProductActionsMenu";
import ProductMobileCard from "@/components/products/ProductMobileCard";
import StorefrontPublisher from "@/components/products/StorefrontPublisher";
import CompletenessBadge from "@/components/products/CompletenessBadge";
import CompletenessSummary from "@/components/products/CompletenessSummary";
import { productCompleteness, COMPLETENESS_FILTERS, matchesCompleteness } from "@/lib/completeness";

export default function Products() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = hasRole(user, "super_admin") || hasRole(user, "inventory");
  const canSeeCost = hasRole(user, "super_admin") || hasRole(user, "inventory");
  const canMakeMovement = hasRole(user, "super_admin") || hasRole(user, "inventory");
  const canSuggest = hasRole(user, "super_admin") || ((user?.capabilities) || []).includes("suggest_photo_changes");
  const canUseAlendra = hasRole(user, "super_admin") || ((user?.capabilities) || []).includes("use_alendra_ai");
  const rowActions = {
    movement: (p) => setMovFor(p),
    edit: (p) => { setEditing(p); setShowForm(true); },
    sales: (p) => setHistoryFor(p),
    history: (p) => setProductHistoryFor(p),
    suggest: (p) => setSuggestFor(p),
    pricing: (p) => setPricingFor(p),
    seo: (p) => setSeoFor(p),
    delete: (p) => setToDelete(p),
  };
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [selectedCats, setSelectedCats] = useState([]);
  const [brand, setBrand] = useState("all");
  const [stockFilter, setStockFilter] = useState("available");
  const [specFilter, setSpecFilter] = useState("all"); // ver COMPLETENESS_FILTERS
  const [editStep, setEditStep] = useState(null);
  const openEditAt = (p, step) => { setEditing(p); setEditStep(step || null); setShowForm(true); };
  const [bulkSpecOpen, setBulkSpecOpen] = useState(false);
  const specSchemas = useSpecSchemas();
  // Advanced filters (server-side)
  const [advOpen, setAdvOpen] = useState(false);
  const [stockMin, setStockMin] = useState("");
  const [stockMax, setStockMax] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [priceField, setPriceField] = useState("store_price");
  // Inventory age filter: "más de N (días|semanas|meses)" counted from product
  // alta (`created`) or from the latest stock entry (`last_entry`).
  const [ageMin, setAgeMin] = useState("");
  const [ageUnit, setAgeUnit] = useState("days");
  const [ageBasis, setAgeBasis] = useState("created");
  // Deep-link from Dashboard: /products?age=90 → preset "más de 90 días".
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const age = searchParams.get("age");
    if (age === null) return;
    if (Number(age) > 0) { setAgeMin(String(Number(age))); setAgeUnit("days"); setAdvOpen(true); }
    const next = new URLSearchParams(searchParams);
    next.delete("age");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, []);
  const AGE_UNIT_DAYS = { days: 1, weeks: 7, months: 30 };
  const ageMinDays = ageMin !== "" && Number(ageMin) > 0 ? Math.round(Number(ageMin) * AGE_UNIT_DAYS[ageUnit]) : 0;
  const ageParams = () => {
    const p = {};
    if (ageMinDays > 0) p.age_min_days = ageMinDays;
    if (ageMinDays > 0 || ageBasis === "last_entry") p.age_basis = ageBasis;
    return p;
  };
  // Per-category stock overrides: { [cat]: { min: "", max: "" } }.
  // Only used when 2+ categories are selected; entries without values fall
  // back to the global Stock mín/máx above.
  const [perCatStock, setPerCatStock] = useState({});
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [movFor, setMovFor] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [lb, setLb] = useState({ open: false, photos: [], title: "" });
  const [historyFor, setHistoryFor] = useState(null);
  const [suggestFor, setSuggestFor] = useState(null);
  const [seoFor, setSeoFor] = useState(null);
  const [pricingFor, setPricingFor] = useState(null);
  const [lotOpen, setLotOpen] = useState(false);
  const [productHistoryFor, setProductHistoryFor] = useState(null);
  // Pestañas: Catálogo (tabla) | Publicador masivo (tienda en línea).
  const tab = searchParams.get("tab") === "publisher" && canEdit ? "publisher" : "catalog";
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "publisher") next.set("tab", "publisher"); else next.delete("tab");
    setSearchParams(next, { replace: true });
  };
  const [pubRefresh, setPubRefresh] = useState(0);
  const openEditById = async (id) => {
    try {
      const { data } = await api.get(`/products/${id}`);
      setEditing(data);
      setShowForm(true);
    } catch {
      toast.error("No se pudo abrir el producto");
    }
  };

  const load = async () => {
    const params = { q, brand };
    if (selectedCats.length > 0) params.categories = selectedCats.join(",");
    if (stockMin !== "") params.stock_min = Number(stockMin);
    if (stockMax !== "") params.stock_max = Number(stockMax);
    if (priceMin !== "") params.price_min = Number(priceMin);
    if (priceMax !== "") params.price_max = Number(priceMax);
    if ((priceMin !== "" || priceMax !== "") && priceField) params.price_field = priceField;
    Object.assign(params, ageParams());
    // Only send per-category stock overrides when 2+ categories are active
    // AND at least one has an explicit value; otherwise let the global range apply.
    if (selectedCats.length >= 2) {
      const payload = selectedCats
        .map((c) => {
          const e = perCatStock[c] || {};
          const item = { cat: c };
          if (e.min !== undefined && e.min !== "") item.min = Number(e.min);
          if (e.max !== undefined && e.max !== "") item.max = Number(e.max);
          return item;
        })
        .filter((it) => it.min !== undefined || it.max !== undefined);
      if (payload.length > 0) params.per_category_stock = JSON.stringify(payload);
    }
    const { data } = await api.get("/products", { params });
    setProducts(data);
  };

  const loadCategories = async () => {
    const r = await api.get("/categories");
    setCategories(r.data);
  };

  useEffect(() => { loadCategories(); }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, selectedCats, brand, stockMin, stockMax, priceMin, priceMax, priceField, perCatStock, ageMinDays, ageBasis]);

  const toggleCat = (c) => {
    setSelectedCats((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
    // When a category is removed, also drop its per-category override to keep
    // state clean; when adding, no-op (defaults to global).
    setPerCatStock((prev) => {
      if (!prev[c]) return prev;
      const next = { ...prev };
      delete next[c];
      return next;
    });
  };
  const clearCats = () => { setSelectedCats([]); setPerCatStock({}); };
  const setPerCat = (c, field, val) => {
    setPerCatStock((prev) => ({
      ...prev,
      [c]: { ...(prev[c] || {}), [field]: val },
    }));
  };
  const clearPerCat = () => setPerCatStock({});
  const catLabel = selectedCats.length === 0
    ? "Todas las categorías"
    : selectedCats.length === 1
      ? selectedCats[0]
      : `${selectedCats.length} categorías`;

  const advCount = [stockMin, stockMax, priceMin, priceMax, ageMin].filter((v) => v !== "").length;
  const clearAdvanced = () => {
    setStockMin(""); setStockMax(""); setPriceMin(""); setPriceMax("");
    setAgeMin(""); setAgeUnit("days"); setAgeBasis("created");
  };
  const AGE_PRESETS = [
    { label: "+30 días", value: "30", unit: "days" },
    { label: "+60 días", value: "60", unit: "days" },
    { label: "+90 días", value: "90", unit: "days" },
    { label: "+6 meses", value: "6", unit: "months" },
  ];
  const applyAgePreset = (pr) => { setAgeMin(pr.value); setAgeUnit(pr.unit); };
  // Days elapsed since the basis date (alta or last stock entry) for a product.
  const ageDaysOf = (p) => {
    const iso = ageBasis === "last_entry" ? (p.last_entry_at || p.created_at) : p.created_at;
    if (!iso) return null;
    return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  };
  const ageTone = (d) => d >= 90
    ? "bg-[#E0000F]/10 text-[#E0000F] border-[#E0000F]/20"
    : d >= 30
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-zinc-100 text-zinc-600 border-zinc-200";
  const ageText = (d) => d === 0 ? "hoy" : d === 1 ? "hace 1 día" : `hace ${d} días`;

  // Aggregate totals over the currently filtered products (post server-side
  // filter + post client stockFilter toggle). Useful when narrowing by
  // spec/category to see how many units total are in stock.
  const stockSummary = useMemo(() => {
    const list = stockFilter === "out"
      ? products.filter((p) => Number(p.stock || 0) <= 0)
      : stockFilter === "available"
        ? products.filter((p) => Number(p.stock || 0) > 0)
        : products;
    const totalStock = list.reduce((acc, p) => acc + Number(p.stock || 0), 0);
    const withStock = list.filter((p) => Number(p.stock || 0) > 0).length;

    // Inventory value per tier = Σ (price × stock), split by currency so we
    // never mix USD costs with MXN sale prices in the same total.
    const tiers = ["cost", "min_price", "reseller_price", "marketplace_price", "store_price"];
    const value = {};
    tiers.forEach((t) => { value[t] = { USD: 0, MXN: 0 }; });
    list.forEach((p) => {
      const st = Number(p.stock || 0);
      if (st <= 0) return;
      const costCur = p.cost_currency || p.currency || "USD";
      const saleCur = p.sale_currency || p.currency || "MXN";
      value.cost[costCur] += Number(p.cost || 0) * st;
      value.min_price[saleCur] += Number(p.min_price || 0) * st;
      value.reseller_price[saleCur] += Number(p.reseller_price || 0) * st;
      value.marketplace_price[saleCur] += Number(p.marketplace_price || 0) * st;
      value.store_price[saleCur] += Number(p.store_price || 0) * st;
    });
    return { totalStock, withStock, count: list.length, value };
  }, [products, stockFilter]);

  const fmtMoney = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const brands = useMemo(() => {
    // Deduplicate case-insensitively (defensive in case backend still has variants).
    const map = new Map();
    products.forEach((p) => {
      const raw = (p.brand || "").trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (!map.has(key)) map.set(key, raw);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [products]);

  // Completitud total (datos, ficha, precios, fotos, tienda) por producto del
  // listado con stock aplicado; el filtro de completitud se aplica encima.
  const stockFiltered = useMemo(() => {
    if (stockFilter === "out") return products.filter((p) => Number(p.stock || 0) <= 0);
    if (stockFilter === "available") return products.filter((p) => Number(p.stock || 0) > 0);
    return products;
  }, [products, stockFilter]);
  const completenessItems = useMemo(
    () => stockFiltered.map((p) => ({ p, c: productCompleteness(p, specSchemas.schemaFor(p.category)) })),
    [stockFiltered, specSchemas]);
  const completenessOf = (p) => completenessItems.find((x) => x.p.id === p.id)?.c || productCompleteness(p, specSchemas.schemaFor(p.category));
  const filteredProducts = useMemo(
    () => completenessItems.filter((x) => matchesCompleteness(x.c, specFilter)).map((x) => x.p),
    [completenessItems, specFilter]);

  const normalizeBrands = async () => {
    try {
      const { data } = await api.post("/products/brands/normalize");
      toast.success(`${data.updated} marca(s) normalizadas`);
      await load();
    } catch {
      toast.error("Error al normalizar marcas");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/products/${toDelete.id}`);
      toast.success("Producto eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    } finally {
      setToDelete(null);
    }
  };

  const downloadExcel = async () => {
    try {
      const params = {};
      if (selectedCats.length > 0) params.categories = selectedCats.join(",");
      if (brand && brand !== "all") params.brand = brand;
      Object.assign(params, ageParams());
      toast.loading("Generando Excel…", { id: "dl" });
      const res = await api.get("/products/export", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const label = selectedCats.length === 1
        ? selectedCats[0].toLowerCase().replace(/\s+/g, "_")
        : selectedCats.length > 1
          ? `${selectedCats.length}_categorias`
          : "todo";
      const filename = `inventario_${label}_${today}.xlsx`;

      // Iframe-safe: open in new tab; browser auto-downloads xlsx mime-type
      const w = window.open(url, "_blank", "noopener");
      if (!w || w.closed) {
        // Popup blocked → fallback to anchor click
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success(`Excel listo: ${filename}`, { id: "dl" });
    } catch (err) {
      toast.error("Error al descargar Excel", { id: "dl" });
    }
  };

  const downloadCatalogPdf = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (selectedCats.length > 0) params.categories = selectedCats.join(",");
      if (brand && brand !== "all") params.brand = brand;
      if (stockMin !== "") params.stock_min = Number(stockMin);
      if (stockMax !== "") params.stock_max = Number(stockMax);
      if (priceMin !== "") params.price_min = Number(priceMin);
      if (priceMax !== "") params.price_max = Number(priceMax);
      if ((priceMin !== "" || priceMax !== "") && priceField) params.price_field = priceField;
      if (stockFilter === "available") params.available_only = true;
      Object.assign(params, ageParams());
      if (selectedCats.length >= 2) {
        const payload = selectedCats
          .map((c) => {
            const e = perCatStock[c] || {};
            const item = { cat: c };
            if (e.min !== undefined && e.min !== "") item.min = Number(e.min);
            if (e.max !== undefined && e.max !== "") item.max = Number(e.max);
            return item;
          })
          .filter((it) => it.min !== undefined || it.max !== undefined);
        if (payload.length > 0) params.per_category_stock = JSON.stringify(payload);
      }
      toast.loading("Generando PDF del catálogo…", { id: "pdf" });
      const res = await api.get("/products/export-pdf", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const label = selectedCats.length === 1
        ? selectedCats[0].toLowerCase().replace(/\s+/g, "_")
        : selectedCats.length > 1
          ? `${selectedCats.length}_categorias`
          : "todo";
      const filename = `catalogo_${label}_${today}.pdf`;
      const w = window.open(url, "_blank", "noopener");
      if (!w || w.closed) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("PDF listo", { id: "pdf" });
    } catch (err) {
      toast.error("Error al generar PDF", { id: "pdf" });
    }
  };

  const fmt = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const cc = (p) => p.cost_currency || p.currency || "USD";
  const sc = (p) => p.sale_currency || p.currency || "MXN";

  // ---------- Share storefront link with current filters ----------
  // Two variants of the link are exposed in the share dialog:
  //   • Con atribución: includes ?v=USER_ID so any sale gets credited to me.
  //   • Sin atribución: clean link, useful for public ads / mass mailing.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareWithVendor, setShareWithVendor] = useState(true);
  const [shareCopied, setShareCopied] = useState(false);
  const buildShareUrl = (withVendor = true) => {
    const base = `${window.location.origin}/catalogo`;
    const qs = new URLSearchParams();
    if (withVendor && user?.id) qs.set("v", user.id);
    if (q) qs.set("q", q);
    // Public storefront supports single-category filter today; if multiple are
    // selected internally we just skip the category param on the shared link.
    if (selectedCats.length === 1) qs.set("category", selectedCats[0]);
    if (brand && brand !== "all") qs.set("brand", brand);
    const s = qs.toString();
    return s ? `${base}?${s}` : base;
  };
  const shareUrl = shareOpen ? buildShareUrl(shareWithVendor) : "";
  const activeFilters = [
    q && `búsqueda "${q}"`,
    selectedCats.length === 1 && `categoría ${selectedCats[0]}`,
    selectedCats.length > 1 && `${selectedCats.length} categorías`,
    brand !== "all" && `marca ${brand}`,
  ].filter(Boolean);
  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setShareCopied(false), 2200);
    } catch {
      window.prompt("Copia el link manualmente:", shareUrl);
    }
  };
  const handleShare = () => {
    setShareCopied(false);
    setShareWithVendor(true);
    setShareOpen(true);
  };

  return (
    <div className="p-4 sm:p-8 space-y-5 sm:space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">CATÁLOGO</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Productos</h1>
          <p className="text-zinc-500 mt-1 text-sm">{tab === "publisher" ? "Publica en la tienda varios productos a la vez" : `${filteredProducts.length} resultados`}</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="new-product-btn"
            className="bg-[#002FA7] hover:bg-[#002277] text-white gap-2 w-full sm:w-auto sm:order-last"
          >
            <Plus size={16} weight="bold" /> Nuevo producto
          </Button>
        )}
        <div className="flex gap-2 overflow-x-auto sm:overflow-visible sm:flex-wrap w-full sm:w-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 sm:pb-0 [&>*]:shrink-0" data-testid="products-toolbar">
          {canEdit && (
            <Button
              onClick={() => navigate("/products/compare")}
              variant="outline"
              className="gap-2 border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/10"
              data-testid="open-compare-btn"
              title="Comparar dos conjuntos de productos lado a lado y editar precios inline"
            >
              <ArrowsLeftRight size={16} weight="bold" />
              Comparar productos
            </Button>
          )}
          <Button
            onClick={handleShare}
            variant="outline"
            className="gap-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 hover:text-[#1eba58]"
            data-testid="share-storefront-btn"
            title={(q || selectedCats.length > 0 || (brand && brand !== "all"))
              ? "Link dinámico: aplica los filtros activos"
              : "Compartir todo el catálogo"}
          >
            <Share size={16} weight="bold" />
            Compartir catálogo
          </Button>
          <Button
            onClick={downloadExcel}
            variant="outline"
            className="gap-2"
            data-testid="download-excel-btn"
            title={selectedCats.length === 1
              ? `Descargar solo categoría: ${selectedCats[0]}`
              : selectedCats.length > 1
                ? `Descargar ${selectedCats.length} categorías filtradas`
                : "Descargar todo el inventario por categorías"}
          >
            <DownloadSimple size={16} weight="bold" />
            {selectedCats.length === 1
              ? `Descargar ${selectedCats[0]}`
              : selectedCats.length > 1
                ? `Descargar (${selectedCats.length})`
                : "Descargar Excel"}
          </Button>
          <Button
            onClick={downloadCatalogPdf}
            variant="outline"
            className="gap-2 border-[#E0000F]/30 text-[#E0000F] hover:bg-[#E0000F]/10 hover:text-[#b8000c]"
            data-testid="download-catalog-pdf-btn"
            title="PDF con fotos, título, precio Tienda y stock — respeta los filtros activos"
          >
            <FilePdf size={16} weight="bold" />
            PDF catálogo
          </Button>
          {canEdit && (
            <Button
              onClick={() => setLotOpen(true)}
              variant="outline"
              className="gap-2 border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="share-lot-btn"
              title="Compartir link temporal (6 días) con la lista filtrada, sin precios individuales, para vender el lote a un mayorista"
            >
              <Package size={16} weight="bold" />
              Compartir lote
            </Button>
          )}
        </div>
      </header>

      {canEdit && (
        <div className="flex gap-1 border-b border-zinc-200" role="tablist" data-testid="products-tabs">
          {[{ key: "catalog", label: "Catálogo" }, { key: "publisher", label: "Publicador masivo", icon: Storefront }].map((t) => (
            <button type="button" key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
              className={`relative px-4 py-2.5 text-sm font-medium inline-flex items-center gap-1.5 transition-colors -mb-px border-b-2 ${tab === t.key ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
              data-testid={`products-tab-${t.key}`}>
              {t.icon && <t.icon size={15} weight={tab === t.key ? "fill" : "regular"} />}{t.label}
            </button>
          ))}
        </div>
      )}

      {tab === "publisher" ? (
        <StorefrontPublisher categories={categories} canUseAlendra={canUseAlendra} onEdit={openEditById} refreshKey={pubRefresh} />
      ) : (<>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3 sm:items-center" data-testid="products-filters">
        <div className="relative col-span-2 sm:flex-1 min-w-0 sm:min-w-[240px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar por nombre, SKU, características…" className="pl-9" data-testid="product-search-input" />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full sm:w-56 justify-between font-normal min-w-0"
              data-testid="category-filter"
              title="Selecciona una o varias categorías"
            >
              <span className={`truncate text-left ${selectedCats.length === 0 ? "text-zinc-500" : "text-zinc-900 font-medium"}`}>
                {catLabel}
              </span>
              <span className="text-zinc-400 text-xs shrink-0 ml-2">
                {selectedCats.length > 0 ? `${selectedCats.length}▾` : "▾"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start" data-testid="category-filter-popover">
            <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b border-zinc-200">
              <span className="text-xs font-semibold text-zinc-700">Categorías</span>
              {selectedCats.length > 0 && (
                <button
                  type="button"
                  onClick={clearCats}
                  className="text-xs text-zinc-500 hover:text-[#E0000F]"
                  data-testid="clear-categories-btn"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto space-y-0.5">
              {categories.length === 0 ? (
                <div className="text-xs text-zinc-400 px-2 py-3 text-center">Sin categorías</div>
              ) : categories.map((c) => (
                <label
                  key={c}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-50 cursor-pointer"
                  data-testid={`category-option-${c}`}
                >
                  <Checkbox
                    checked={selectedCats.includes(c)}
                    onCheckedChange={() => toggleCat(c)}
                  />
                  <span className="text-sm text-zinc-800 flex-1 truncate">{c}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="w-full sm:w-44" data-testid="brand-filter"><SelectValue placeholder="Marca"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las marcas</SelectItem>
            {brands.map((b)=><SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stockFilter} onValueChange={setStockFilter}>
          <SelectTrigger className="w-full sm:w-44" data-testid="stock-filter"><SelectValue placeholder="Disponibilidad"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="available" data-testid="stock-filter-available">Disponibles</SelectItem>
            <SelectItem value="out" data-testid="stock-filter-out">Sin stock</SelectItem>
            <SelectItem value="all" data-testid="stock-filter-all">Todos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={specFilter} onValueChange={setSpecFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="spec-filter"><SelectValue placeholder="Completitud"/></SelectTrigger>
          <SelectContent>
            {COMPLETENESS_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value} data-testid={`spec-filter-${f.value}`}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canUseAlendra && (
          <Button type="button" size="sm" onClick={() => setBulkSpecOpen(true)}
            className="h-9 gap-1.5 text-white bg-gradient-to-br from-[#002FA7] to-[#003ed4] hover:shadow-md col-span-2 sm:col-auto"
            title="Alendra AI sugiere la ficha técnica de los productos incompletos del listado actual" data-testid="bulk-spec-ai-btn">
            <Sparkle size={14} weight="fill" /> Completar fichas con IA
          </Button>
        )}
        <BulkSpecAIDialog open={bulkSpecOpen} onOpenChange={setBulkSpecOpen} products={filteredProducts} onApplied={load} />
        {hasRole(user, "super_admin") && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={normalizeBrands}
            className="text-xs text-zinc-500 hover:text-[#002FA7] gap-1 justify-start sm:justify-center"
            title="Limpia espacios y unifica mayúsculas/minúsculas en marcas duplicadas"
            data-testid="normalize-brands-btn"
          >
            <ArrowsClockwise size={12} weight="bold" /> Limpiar marcas duplicadas
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setAdvOpen((v) => !v)}
          className={`text-xs gap-1 justify-start sm:justify-center ${advCount > 0 ? "text-[#002FA7] font-semibold" : "text-zinc-500"} hover:text-[#002FA7]`}
          title="Filtros avanzados: stock, rango de precios y antigüedad en inventario"
          data-testid="toggle-advanced-filters"
        >
          <FunnelSimple size={12} weight="bold" />
          Filtros avanzados
          {advCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#002FA7] text-white text-[10px] font-bold">
              {advCount}
            </span>
          )}
        </Button>
      </div>

      {selectedCats.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center" data-testid="selected-categories-chips">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mr-1">Categorías activas:</span>
          {selectedCats.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => toggleCat(c)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/20 text-xs font-medium hover:bg-[#002FA7]/20 transition-colors"
              title={`Quitar "${c}"`}
              data-testid={`chip-${c}`}
            >
              {c}
              <X size={11} weight="bold" />
            </button>
          ))}
          {selectedCats.length > 1 && (
            <button
              type="button"
              onClick={clearCats}
              className="text-[11px] text-zinc-500 hover:text-[#E0000F] ml-1 underline underline-offset-2"
              data-testid="clear-all-chips-btn"
            >
              Quitar todas
            </button>
          )}
        </div>
      )}

      {advOpen && (
        <div className="border border-zinc-200 bg-zinc-50 rounded-md p-4 space-y-3" data-testid="advanced-filters-panel">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
              <FunnelSimple size={12} weight="bold" /> Filtros avanzados
            </div>
            <div className="flex items-center gap-3">
              <div className="text-xs text-zinc-600 flex items-center gap-1.5" data-testid="filter-stock-total">
                <span className="uppercase tracking-wider text-[10px] text-zinc-500 font-semibold">Stock total:</span>
                <span className="font-heading font-extrabold text-[#002FA7] text-base numeric">
                  {stockSummary.totalStock.toLocaleString("es-MX")}
                </span>
                <span className="text-[11px] text-zinc-500">
                  pz en {stockSummary.withStock} de {stockSummary.count} producto{stockSummary.count === 1 ? "" : "s"}
                </span>
              </div>
              {advCount > 0 && (
                <button
                  type="button"
                  onClick={clearAdvanced}
                  className="text-xs text-zinc-500 hover:text-[#E0000F] inline-flex items-center gap-1"
                  data-testid="clear-advanced-filters"
                >
                  <X size={12} weight="bold" /> Limpiar
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-600">Stock mínimo</Label>
              <Input
                type="number"
                min="0"
                value={stockMin}
                onChange={(e) => setStockMin(e.target.value)}
                placeholder="ej. 5"
                className="bg-white"
                data-testid="filter-stock-min"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-600">Stock máximo</Label>
              <Input
                type="number"
                min="0"
                value={stockMax}
                onChange={(e) => setStockMax(e.target.value)}
                placeholder="ej. 20"
                className="bg-white"
                data-testid="filter-stock-max"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-600">Precio mínimo</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                placeholder="ej. 1000"
                className="bg-white"
                data-testid="filter-price-min"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-600">Precio máximo</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                placeholder="ej. 15000"
                className="bg-white"
                data-testid="filter-price-max"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-zinc-600 whitespace-nowrap">Aplicar rango de precio a:</Label>
            <Select value={priceField} onValueChange={setPriceField}>
              <SelectTrigger className="w-56 bg-white h-9" data-testid="filter-price-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="store_price">Precio Tienda</SelectItem>
                <SelectItem value="marketplace_price">Distribuidor</SelectItem>
                <SelectItem value="reseller_price">Reventa</SelectItem>
                <SelectItem value="min_price">Precio mínimo</SelectItem>
                {canSeeCost && <SelectItem value="cost">Costo</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          {/* Antigüedad en inventario */}
          <div className="border-t border-zinc-200 pt-3 space-y-2" data-testid="age-filter-panel">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
              <Clock size={12} weight="bold" /> Antigüedad en inventario
              <span className="normal-case text-zinc-400 font-normal ml-1">
                (productos que llevan más tiempo sin venderse)
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-xs text-zinc-600 whitespace-nowrap">Más de</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={ageMin}
                onChange={(e) => setAgeMin(e.target.value)}
                placeholder="ej. 30"
                className="bg-white w-24 h-9"
                data-testid="filter-age-min"
              />
              <Select value={ageUnit} onValueChange={setAgeUnit}>
                <SelectTrigger className="w-32 bg-white h-9" data-testid="filter-age-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="days">días</SelectItem>
                  <SelectItem value="weeks">semanas</SelectItem>
                  <SelectItem value="months">meses</SelectItem>
                </SelectContent>
              </Select>
              <Label className="text-xs text-zinc-600 whitespace-nowrap ml-2">contando desde</Label>
              <Select value={ageBasis} onValueChange={setAgeBasis}>
                <SelectTrigger className="w-52 bg-white h-9" data-testid="filter-age-basis">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created">Alta del producto</SelectItem>
                  <SelectItem value="last_entry">Última entrada de stock</SelectItem>
                </SelectContent>
              </Select>
              {ageMinDays > 0 && (
                <span className="text-[11px] text-[#002FA7] font-medium" data-testid="filter-age-summary">
                  = {ageMinDays} días o más
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mr-1">Atajos:</span>
              {AGE_PRESETS.map((pr) => {
                const active = ageMin === pr.value && ageUnit === pr.unit;
                return (
                  <button
                    key={pr.label}
                    type="button"
                    onClick={() => applyAgePreset(pr)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active
                      ? "bg-[#002FA7] text-white border-[#002FA7]"
                      : "bg-white text-zinc-700 border-zinc-200 hover:border-[#002FA7] hover:text-[#002FA7]"}`}
                    data-testid={`age-preset-${pr.value}-${pr.unit}`}
                  >
                    {pr.label}
                  </button>
                );
              })}
              {ageMin !== "" && (
                <button
                  type="button"
                  onClick={() => setAgeMin("")}
                  className="text-xs text-zinc-500 hover:text-[#E0000F] inline-flex items-center gap-1 ml-1"
                  data-testid="clear-age-filter"
                >
                  <X size={11} weight="bold" /> Quitar
                </button>
              )}
            </div>
          </div>

          {/* Stock por categoría (solo cuando hay 2+ categorías seleccionadas) */}
          {selectedCats.length >= 2 && (
            <div className="border-t border-zinc-200 pt-3" data-testid="per-category-stock-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
                  <FunnelSimple size={12} weight="bold" /> Stock por categoría
                  <span className="normal-case text-zinc-400 font-normal ml-1">
                    (deja vacío para usar el rango general)
                  </span>
                </div>
                {Object.keys(perCatStock).length > 0 && (
                  <button
                    type="button"
                    onClick={clearPerCat}
                    className="text-xs text-zinc-500 hover:text-[#E0000F] inline-flex items-center gap-1"
                    data-testid="clear-per-cat-stock"
                  >
                    <X size={11} weight="bold" /> Limpiar
                  </button>
                )}
              </div>
              <div className="rounded-md border border-zinc-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-100 text-[10px] uppercase text-zinc-500">
                    <tr>
                      <th className="text-left px-3 py-1.5 font-semibold">Categoría</th>
                      <th className="text-left px-3 py-1.5 font-semibold w-40">Stock mínimo</th>
                      <th className="text-left px-3 py-1.5 font-semibold w-40">Stock máximo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCats.map((c) => {
                      const e = perCatStock[c] || {};
                      return (
                        <tr key={c} className="border-t border-zinc-100 bg-white" data-testid={`per-cat-row-${c}`}>
                          <td className="px-3 py-1.5 font-medium text-zinc-800">{c}</td>
                          <td className="px-3 py-1.5">
                            <Input
                              type="number"
                              min="0"
                              value={e.min ?? ""}
                              onChange={(ev) => setPerCat(c, "min", ev.target.value)}
                              placeholder={stockMin !== "" ? `Global: ${stockMin}` : "Sin mínimo"}
                              className="h-8 text-xs bg-white"
                              data-testid={`per-cat-min-${c}`}
                            />
                          </td>
                          <td className="px-3 py-1.5">
                            <Input
                              type="number"
                              min="0"
                              value={e.max ?? ""}
                              onChange={(ev) => setPerCat(c, "max", ev.target.value)}
                              placeholder={stockMax !== "" ? `Global: ${stockMax}` : "Sin máximo"}
                              className="h-8 text-xs bg-white"
                              data-testid={`per-cat-max-${c}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Totales por tier (valor inventario = precio × stock) */}
          <div className="border-t border-zinc-200 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">
              Valor inventario (precio × stock)
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {canSeeCost && (
                <TierStat
                  label="Costo"
                  usd={stockSummary.value.cost.USD}
                  mxn={stockSummary.value.cost.MXN}
                  fmt={fmtMoney}
                  testid="tier-total-cost"
                />
              )}
              {canSeeCost && (
                <TierStat
                  label="Mínimo"
                  usd={stockSummary.value.min_price.USD}
                  mxn={stockSummary.value.min_price.MXN}
                  fmt={fmtMoney}
                  testid="tier-total-min"
                />
              )}
              <TierStat
                label="Reventa"
                usd={stockSummary.value.reseller_price.USD}
                mxn={stockSummary.value.reseller_price.MXN}
                fmt={fmtMoney}
                testid="tier-total-reseller"
              />
              <TierStat
                label="Distribuidor"
                usd={stockSummary.value.marketplace_price.USD}
                mxn={stockSummary.value.marketplace_price.MXN}
                fmt={fmtMoney}
                testid="tier-total-marketplace"
              />
              <TierStat
                label="Tienda"
                usd={stockSummary.value.store_price.USD}
                mxn={stockSummary.value.store_price.MXN}
                fmt={fmtMoney}
                highlight
                testid="tier-total-store"
              />
            </div>
          </div>
        </div>
      )}

      {canEdit && <CompletenessSummary items={completenessItems} filter={specFilter} onFilter={setSpecFilter} />}

      <div className="hidden md:block border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="products-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3 sticky left-0 z-10 bg-zinc-50 border-r border-zinc-100">Producto</th>
              <th className="text-left px-4 py-3">Link de fotos</th>
              <th className="text-right px-4 py-3">Stock</th>
              {canSeeCost && <th className="text-right px-4 py-3">Costo</th>}
              {canSeeCost && <th className="text-right px-4 py-3">Precio mínimo</th>}
              <th className="text-right px-4 py-3">Reventa</th>
              <th className="text-right px-4 py-3">Distribuidor</th>
              <th className="text-right px-4 py-3">Tienda</th>
              <th className="text-left px-4 py-3">Categoría</th>
              <th className="text-left px-4 py-3">Marca</th>
              <th className="text-left px-4 py-3">Alta</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 && (
              <tr><td colSpan={canSeeCost ? 11 : 9} className="px-4 py-16 text-center text-zinc-400 dotted-bg">
                {stockFilter === "available" ? "Sin productos disponibles. Cambia el filtro a “Todos” para ver agotados." : "Sin productos."}
              </td></tr>
            )}
            {filteredProducts.map((p) => (
              <tr key={p.id} className="group border-t border-zinc-100 hover:bg-zinc-50 align-top" data-testid={`product-row-${p.id}`}>
                <td className="px-4 py-3 max-w-[280px] min-w-[220px] sticky left-0 z-[5] bg-white group-hover:bg-zinc-50 border-r border-zinc-100">
                  {(canEdit || canSuggest || canMakeMovement) && (
                    <span className="float-right -mr-2 -mt-1">
                      <ProductActionsMenu product={p} perms={{ canEdit, canSuggest, canUseAlendra, canMakeMovement }} on={rowActions} testid={`row-menu-${p.id}`} />
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setHistoryFor(p)}
                    className="font-medium text-left hover:text-[#002FA7] hover:underline decoration-dotted underline-offset-2 inline-flex items-center gap-1.5 group cursor-pointer"
                    title="Ver en qué notas de venta se ha vendido"
                    data-testid={`product-name-${p.id}`}
                  >
                    {p.name}
                    <Receipt size={13} weight="duotone" className="text-zinc-300 group-hover:text-[#002FA7] transition-colors" />
                  </button>
                  {p.aesthetic_grade && (
                    <span
                      className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-sm text-[10px] font-heading font-extrabold ${
                        p.aesthetic_grade === "A" ? "bg-emerald-100 text-emerald-700" :
                        p.aesthetic_grade === "B" ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-[#E0000F]"
                      }`}
                      title={
                        p.aesthetic_grade === "A" ? "Estética A · Excelente" :
                        p.aesthetic_grade === "B" ? "Estética B · Buena (detalles menores)" :
                        "Estética C · Con detalles visibles"
                      }
                      data-testid={`aesthetic-${p.id}`}
                    >
                      {p.aesthetic_grade}
                    </span>
                  )}
                  {p.characteristics && (
                    <div
                      className="text-xs text-zinc-500 mt-0.5 line-clamp-2 leading-snug"
                      title={p.characteristics}
                    >
                      {p.characteristics}
                    </div>
                  )}
                  {p.sku && <div className="text-[11px] font-mono-data text-zinc-400 mt-0.5">SKU: {p.sku}</div>}
                  <CompletenessBadge completeness={completenessOf(p)} published={!!p.show_in_storefront} onFix={canEdit ? (step) => openEditAt(p, step) : undefined} testid={`readiness-badge-${p.id}`} />
                </td>
                <td className="px-4 py-3">
                  {(() => {
                    const photos = (Array.isArray(p.photos) ? p.photos.filter(Boolean) : []);
                    if (photos.length === 0 && p.photos_url) photos.push(p.photos_url);
                    if (photos.length === 0) return <span className="text-xs text-zinc-300">—</span>;
                    const cover = (p.cover_photo || "").trim() || photos[0];
                    return (
                      <button
                        type="button"
                        onClick={() => setLb({ open: true, photos, title: p.name })}
                        className="inline-flex items-center gap-2 text-xs text-[#002FA7] hover:underline whitespace-nowrap cursor-pointer"
                        data-testid={`photos-link-${p.id}`}
                        title={`Ver ${photos.length} foto${photos.length === 1 ? "" : "s"} del producto`}
                      >
                        <span className="relative inline-block">
                          <img src={cover} alt="" className="w-8 h-8 object-cover rounded border border-zinc-200" />
                          {photos.length > 1 && (
                            <span className="absolute -top-1 -right-1 bg-[#002FA7] text-white text-[8px] font-bold leading-none rounded-full px-1 py-[1px] min-w-[14px] text-center">
                              {photos.length}
                            </span>
                          )}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <ImageIcon size={12} weight="bold"/>
                          Ver fotos
                        </span>
                      </button>
                    );
                  })()}
                </td>
                <td className={`px-4 py-3 text-right numeric font-semibold ${p.stock <= p.low_stock_threshold ? "text-[#E0000F]" : "text-zinc-900"}`}>
                  {p.stock}
                  {p.hospital_qty > 0 && (
                    <span className="ml-1.5 text-[9px] font-mono-data font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-700" title={`${p.hospital_qty} en taller`}>
                      🏥{p.hospital_qty}
                    </span>
                  )}
                </td>
                {canSeeCost && <td className="px-4 py-3 text-right numeric text-zinc-700">{fmt(p.cost, cc(p))}</td>}
                {canSeeCost && <td className="px-4 py-3 text-right numeric text-zinc-700">{fmt(p.min_price, sc(p))}</td>}
                <td className="px-4 py-3 text-right numeric text-zinc-700">{fmt(p.reseller_price, sc(p))}</td>
                <td className="px-4 py-3 text-right numeric text-zinc-700">{fmt(p.marketplace_price, sc(p))}</td>
                <td className="px-4 py-3 text-right numeric font-semibold">{fmt(p.store_price, sc(p))}</td>
                <td className="px-4 py-3 text-zinc-700">{p.category}</td>
                <td className="px-4 py-3 text-zinc-700">{p.brand || "—"}</td>
                <td className="px-4 py-3 text-zinc-600 text-xs whitespace-nowrap" data-testid={`product-created-${p.id}`} title={`${p.created_at ? new Date(p.created_at).toLocaleString("es-MX") : ""}${p.created_by_email ? " — " + p.created_by_email : ""}`}>
                  <div>{p.created_at ? new Date(p.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                  {(() => {
                    const d = ageDaysOf(p);
                    if (d === null) return null;
                    return (
                      <span
                        className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold numeric ${ageTone(d)}`}
                        title={ageBasis === "last_entry" ? "Días desde la última entrada de stock" : "Días desde el alta del producto"}
                        data-testid={`product-age-${p.id}`}
                      >
                        <Clock size={10} weight="bold" /> {ageText(d)}
                      </span>
                    );
                  })()}
                  {p.created_by_name && (
                    <div className="text-zinc-400 text-[10px] mt-0.5 truncate max-w-[110px]" title={p.created_by_email || p.created_by_name}>
                      por {p.created_by_name}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Móvil: tarjetas en lugar de tabla */}
      <div className="md:hidden space-y-3" data-testid="products-cards">
        {filteredProducts.length === 0 && (
          <div className="border border-zinc-200 bg-white px-4 py-12 text-center text-zinc-400 text-sm dotted-bg">
            {stockFilter === "available" ? "Sin productos disponibles. Cambia el filtro a “Todos” para ver agotados." : "Sin productos."}
          </div>
        )}
        {filteredProducts.map((p) => {
          const d = ageDaysOf(p);
          return (
            <ProductMobileCard
              key={p.id}
              p={p}
              perms={{ canEdit, canSuggest, canUseAlendra, canMakeMovement }}
              on={rowActions}
              canSeeCost={canSeeCost}
              completeness={completenessOf(p)}
              onFix={canEdit ? (step) => openEditAt(p, step) : undefined}
              age={d === null ? null : { tone: ageTone(d), text: ageText(d) }}
              onPhotos={(photos) => setLb({ open: true, photos, title: p.name })}
              onName={(prod) => setHistoryFor(prod)}
            />
          );
        })}
      </div>
      </>)}

      {showForm && (
        <ProductForm
          product={editing}
          initialStep={editStep}
          categories={categories}
          onClose={()=>{ setShowForm(false); setEditStep(null); }}
          onSaved={()=>{ setShowForm(false); setEditStep(null); load(); setPubRefresh((k) => k + 1); }}
          onCategoriesChanged={loadCategories}
          onSelectExisting={(p) => {
            setShowForm(false);
            setMovFor(p);
            toast.info(`"${p.name}" ya está registrado — solo agrega más stock`);
          }}
        />
      )}
      {movFor && (
        <MovementForm
          product={movFor}
          onClose={()=>setMovFor(null)}
          onDone={()=>{ setMovFor(null); load(); }}
        />
      )}

      {seoFor && (
        <AlendraSeoModal
          productId={seoFor.id}
          productName={seoFor.name}
          onClose={()=>setSeoFor(null)}
        />
      )}

      {pricingFor && (
        <AlendraPricingModal
          productId={pricingFor.id}
          productName={pricingFor.name}
          currentStorePrice={pricingFor.store_price}
          onClose={()=>setPricingFor(null)}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará <span className="font-semibold">{toDelete?.name}</span> y todos sus movimientos asociados. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-confirm-btn"
              className="bg-[#E0000F] hover:bg-[#b8000c] text-white"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PhotoLightbox
        open={lb.open}
        onClose={() => setLb((s) => ({ ...s, open: false }))}
        photos={lb.photos}
        title={lb.title}
        filenameBase={lb.title}
      />

      {/* Share storefront dialog — shows the generated link, copy/open/WhatsApp shortcuts */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-lg" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Share size={22} weight="bold" className="text-[#25D366]" />
              Link de tu tienda
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-zinc-600">
              {activeFilters.length === 0 ? (
                <>Este link abre tu tienda con <b>todo el catálogo</b>.</>
              ) : (
                <>Este link abre tu tienda con: <b>{activeFilters.join(" · ")}</b>. Si cambias los filtros aquí, el link cambiará dinámicamente.</>
              )}
            </div>

            {/* Toggle: Con atribución vs Sin atribución */}
            <div className="inline-flex rounded-full bg-zinc-100 p-1 text-xs font-semibold" role="tablist" data-testid="share-mode-toggle">
              <button
                type="button"
                role="tab"
                aria-selected={shareWithVendor}
                onClick={() => { setShareWithVendor(true); setShareCopied(false); }}
                className={`px-3 py-1.5 rounded-full transition-colors ${shareWithVendor ? "bg-white text-[#002FA7] shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}
                data-testid="share-mode-with-vendor"
              >
                Con mi atribución
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!shareWithVendor}
                onClick={() => { setShareWithVendor(false); setShareCopied(false); }}
                className={`px-3 py-1.5 rounded-full transition-colors ${!shareWithVendor ? "bg-white text-[#002FA7] shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}
                data-testid="share-mode-clean"
              >
                Sin atribución
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 -mt-2">
              {shareWithVendor
                ? <>Cuando un cliente entre por este link, la venta se atribuirá a ti (<code className="bg-zinc-100 px-1 rounded">?v=</code> tu ID).</>
                : <>Link público neutral — útil para campañas, mailings o cuando NO quieres atribución personal.</>}
            </p>

            <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50 break-all font-mono-data text-[11px] text-zinc-800 leading-relaxed select-all" data-testid="share-url-box">
              {shareUrl}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Button onClick={copyShareUrl} className="bg-[#002FA7] hover:bg-[#002277] text-white gap-2" data-testid="share-copy-btn">
                {shareCopied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
                {shareCopied ? "Copiado" : "Copiar"}
              </Button>
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-sm font-medium"
                data-testid="share-open-btn"
              >
                <ArrowSquareOut size={14} weight="bold" /> Abrir
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Hola! Te comparto nuestro catálogo Alendra PC: ${shareUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md bg-[#25D366] hover:bg-[#1eba58] text-white text-sm font-semibold"
                data-testid="share-whatsapp-btn"
              >
                <WhatsappLogo size={14} weight="fill" /> WhatsApp
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent("Catálogo Alendra PC")}&body=${encodeURIComponent(`Hola! Te comparto nuestro catálogo: ${shareUrl}`)}`}
                className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-sm font-medium"
                data-testid="share-email-btn"
              >
                Email
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SalesHistoryDialog product={historyFor} onClose={() => setHistoryFor(null)} onChanged={load} />
      <SuggestPhotoDialog product={suggestFor} onClose={() => setSuggestFor(null)} />
      <ShareLotDialog open={lotOpen} onOpenChange={setLotOpen} products={filteredProducts} />
      <ProductHistoryDialog product={productHistoryFor} onClose={() => setProductHistoryFor(null)} />
    </div>
  );
}

function SalesHistoryDialog({ product, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tallerHistory, setTallerHistory] = useState([]);
  const [tallerOpen, setTallerOpen] = useState(false);
  const [showTaller, setShowTaller] = useState(false);
  const [tallerForm, setTallerForm] = useState({ fault_description: "", location: "", priority: "normal" });
  const [tallerSending, setTallerSending] = useState(false);

  useEffect(() => {
    if (!product) { setData(null); setShowTaller(false); setTallerHistory([]); setTallerOpen(false); return; }
    setLoading(true);
    api.get(`/products/${product.id}/sales-history`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Error cargando historial"))
      .finally(() => setLoading(false));
    api.get(`/hospital/product/${product.id}/history`)
      .then((r) => setTallerHistory(r.data || []))
      .catch(() => setTallerHistory([]));
  }, [product]);

  const canSendToTaller = !!product && (hasRole(user, "super_admin") || hasRole(user, "inventory") || hasRole(user, "technician") || hasRole(user, "taller"));
  const availableStock = product ? (Number(product.stock || 0) - Number(product.hospital_qty || 0)) : 0;

  const sendToTaller = async () => {
    if (!tallerForm.fault_description.trim()) {
      toast.error("Describe la falla");
      return;
    }
    setTallerSending(true);
    try {
      await api.post("/hospital/tickets", {
        product_id: product.id,
        reason: tallerForm.fault_description.trim(),
        fault_description: tallerForm.fault_description.trim(),
        location: tallerForm.location.trim() || null,
        priority: tallerForm.priority,
        source: "piso_de_venta",
      });
      toast.success("Equipo enviado al taller · stock bloqueado");
      setShowTaller(false);
      setTallerForm({ fault_description: "", location: "", priority: "normal" });
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar al taller");
    } finally {
      setTallerSending(false);
    }
  };

  const open = !!product;
  const totals = data?.totals;
  const rows = data?.rows || [];
  const currency = product?.sale_currency || "MXN";
  const fmt = (v, c) => new Intl.NumberFormat("es-MX", { style: "currency", currency: c || currency, minimumFractionDigits: 2 }).format(Number(v || 0));
  const statusBadge = (s, r) => {
    // Prefer the explicit flags from the API when available so quotes and
    // canceled notes look distinct even if the raw status is legacy.
    if (r?.is_canceled) {
      return <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded bg-red-50 text-red-700 border-red-200">Cancelada</span>;
    }
    if (r?.is_quote) {
      return <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded bg-violet-50 text-violet-700 border-violet-200">En cotización</span>;
    }
    const map = {
      paid:      { label: "Pagada",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      partial:   { label: "Parcial",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
      sent:      { label: "Enviada",  cls: "bg-blue-50 text-blue-700 border-blue-200" },
      overdue:   { label: "Vencida",  cls: "bg-red-50 text-red-700 border-red-200" },
      draft:     { label: "Borrador", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
      pending:   { label: "Pendiente",cls: "bg-blue-50 text-blue-700 border-blue-200" },
      canceled:  { label: "Cancelada",cls: "bg-red-50 text-red-700 border-red-200" },
      cancelled: { label: "Cancelada",cls: "bg-red-50 text-red-700 border-red-200" },
      quote:     { label: "En cotización", cls: "bg-violet-50 text-violet-700 border-violet-200" },
      accepted:  { label: "Aceptada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    };
    const m = map[s] || { label: s || "—", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" };
    return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded ${m.cls}`}>{m.label}</span>;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            <Receipt size={22} weight="duotone" className="text-[#002FA7]" />
            Acciones de item
          </DialogTitle>
        </DialogHeader>
        {product && (
          <div className="space-y-4">
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-900">{product.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-3">
                  {product.brand && <span>{product.brand}</span>}
                  {product.sku && <span className="font-mono-data">SKU: {product.sku}</span>}
                  <span className="font-mono-data">Stock disponible: {availableStock}{product.hospital_qty ? ` (${product.hospital_qty} en taller)` : ""}</span>
                </div>
              </div>
              {canSendToTaller && !showTaller && (
                <Button
                  variant="outline"
                  onClick={() => setShowTaller(true)}
                  disabled={availableStock <= 0}
                  title={availableStock <= 0 ? "Sin stock disponible para enviar al taller" : "Envía una unidad al Taller y Reparación"}
                  className="gap-1.5 border-[#E0000F]/40 text-[#E0000F] hover:bg-[#E0000F]/5 hover:border-[#E0000F] shrink-0"
                  data-testid="history-send-taller-btn"
                >
                  <FirstAidKit size={14} weight="bold" />
                  Enviar al taller
                </Button>
              )}
            </div>

            {showTaller && (
              <div className="border border-[#E0000F]/30 bg-[#E0000F]/5 rounded-md p-3 space-y-3" data-testid="history-taller-form">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-[#E0000F] inline-flex items-center gap-1.5">
                    <FirstAidKit size={12} weight="fill" /> Enviar al Taller y Reparación
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTaller(false)}
                    className="text-zinc-400 hover:text-zinc-700"
                    aria-label="Cerrar"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Descripción de la falla *</Label>
                  <Textarea
                    value={tallerForm.fault_description}
                    onChange={(e) => setTallerForm({ ...tallerForm, fault_description: e.target.value })}
                    rows={3}
                    placeholder="No enciende, teclado falla, pantalla muerta, errores de sistema…"
                    className="mt-1"
                    data-testid="history-taller-fault"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Ubicación física</Label>
                    <Textarea
                      value={tallerForm.location}
                      onChange={(e) => setTallerForm({ ...tallerForm, location: e.target.value })}
                      rows={2}
                      placeholder="Mesa 2, Estante B…"
                      className="mt-1"
                      data-testid="history-taller-location"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Prioridad</Label>
                    <Select value={tallerForm.priority} onValueChange={(v) => setTallerForm({ ...tallerForm, priority: v })}>
                      <SelectTrigger className="mt-1" data-testid="history-taller-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgent">🔴 Urgente</SelectItem>
                        <SelectItem value="high">🟠 Alta</SelectItem>
                        <SelectItem value="normal">🔵 Normal</SelectItem>
                        <SelectItem value="low">⚪ Baja</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowTaller(false)} disabled={tallerSending}>Cancelar</Button>
                  <Button onClick={sendToTaller} disabled={tallerSending} className="bg-[#E0000F] hover:bg-[#c30414] text-white gap-1.5" data-testid="history-taller-submit">
                    <FirstAidKit size={14} weight="bold" />
                    {tallerSending ? "Enviando…" : "Confirmar envío"}
                  </Button>
                </div>
              </div>
            )}

            {tallerHistory.length > 0 && (
              <section className="border border-zinc-200 rounded-md" data-testid="taller-history-section">
                <button
                  type="button"
                  onClick={() => setTallerOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-zinc-50 hover:bg-zinc-100 transition-colors rounded-t-md"
                  data-testid="taller-history-toggle"
                >
                  <div className="flex items-center gap-2">
                    <FirstAidKit size={14} weight="fill" className="text-[#E0000F]" />
                    <span className="text-xs uppercase tracking-widest font-semibold text-zinc-700">
                      Historial en Taller y Reparación · {tallerHistory.length} paso{tallerHistory.length !== 1 ? "s" : ""}
                    </span>
                    <span className="text-[10px] text-zinc-500 italic normal-case tracking-normal ml-1">— información interna, no compartir al cliente</span>
                  </div>
                  <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
                    {tallerOpen ? "Ocultar" : "Ver detalle"}
                  </span>
                </button>
                {tallerOpen && (
                  <div className="p-3 space-y-2 max-h-64 overflow-y-auto" data-testid="taller-history-list">
                    {tallerHistory.map((t) => {
                      const created = t.created_at ? new Date(t.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";
                      const closed = t.closed_at ? new Date(t.closed_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "abierto";
                      const stMap = {
                        pendiente_diagnostico: { label: "Pendiente diagnóstico", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                        en_diagnostico: { label: "En diagnóstico", cls: "bg-blue-50 text-blue-700 border-blue-200" },
                        listo: { label: "Listo · alta", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                        pendiente_refaccion: { label: "Pendiente refacción", cls: "bg-orange-50 text-orange-700 border-orange-200" },
                        irreparable: { label: "Irreparable", cls: "bg-red-50 text-red-700 border-red-200" },
                        baja_definitiva: { label: "Baja definitiva", cls: "bg-red-50 text-red-700 border-red-200" },
                        en_espera_autorizacion: { label: "En espera autorización", cls: "bg-zinc-50 text-zinc-700 border-zinc-200" },
                        enviado_externo: { label: "Enviado externo", cls: "bg-violet-50 text-violet-700 border-violet-200" },
                      };
                      const st = stMap[t.status] || { label: t.status, cls: "bg-zinc-50 text-zinc-700 border-zinc-200" };
                      return (
                        <div key={t.id} className="border border-zinc-200 rounded-sm p-2.5 bg-white text-xs" data-testid={`taller-ticket-${t.id}`}>
                          <div className="flex items-baseline justify-between gap-2 flex-wrap mb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded ${st.cls}`}>{st.label}</span>
                              <span className="font-mono-data text-[10px] text-zinc-500">#{t.id.slice(0, 8)}</span>
                            </div>
                            <div className="font-mono-data text-[10px] text-zinc-500">{created} → {closed}</div>
                          </div>
                          <div className="text-zinc-700"><span className="text-zinc-500 font-mono-data text-[10px] uppercase tracking-widest">Falla:</span> {t.fault_description || t.reason || "—"}</div>
                          {t.diagnosis && (
                            <div className="text-zinc-700 mt-0.5"><span className="text-zinc-500 font-mono-data text-[10px] uppercase tracking-widest">Diagnóstico:</span> {t.diagnosis}</div>
                          )}
                          {t.actions_taken && (
                            <div className="text-zinc-700 mt-0.5"><span className="text-zinc-500 font-mono-data text-[10px] uppercase tracking-widest">Acciones:</span> {t.actions_taken}</div>
                          )}
                          {t.parts_used && (
                            <div className="text-zinc-700 mt-0.5"><span className="text-zinc-500 font-mono-data text-[10px] uppercase tracking-widest">Refacciones:</span> {t.parts_used}</div>
                          )}
                          {t.observations && (
                            <div className="text-zinc-700 mt-0.5 italic"><span className="not-italic text-zinc-500 font-mono-data text-[10px] uppercase tracking-widest">Observaciones:</span> {t.observations}</div>
                          )}
                          <div className="mt-1 flex items-center gap-3 text-[10px] text-zinc-500 font-mono-data">
                            {t.technician_name && <span>👨‍🔧 {t.technician_name}</span>}
                            {t.repair_cost > 0 && <span className="text-[#E0000F] font-semibold">${Number(t.repair_cost).toFixed(2)}</span>}
                            {t.minutes_invested > 0 && <span>{t.minutes_invested} min</span>}
                            {t.source && <span className="uppercase">{t.source.replace(/_/g, " ")}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {!loading && totals && (
              <div className="space-y-2" data-testid="history-totals">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <SummaryStat label="Notas con este producto" value={totals.notes_count} />
                  <SummaryStat
                    label="Unidades vendidas"
                    value={totals.units_sold}
                    sub="solo notas activas (no incluye cotización ni canceladas)"
                  />
                  <SummaryStat
                    label="Ingresos generados"
                    value={fmt(totals.revenue)}
                    highlight
                    sub="solo de ventas confirmadas"
                  />
                  <div className="p-3 rounded-md border border-zinc-200 bg-white">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">No cuentan como venta</div>
                    <div className="mt-1 flex items-center gap-3 flex-wrap">
                      <div className="inline-flex items-center gap-1.5">
                        <span className="text-[10px] uppercase font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">
                          En cotización
                        </span>
                        <span className="font-heading font-extrabold text-lg text-violet-700 numeric">
                          {totals.units_in_quote || 0}
                        </span>
                      </div>
                      <div className="inline-flex items-center gap-1.5">
                        <span className="text-[10px] uppercase font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                          Canceladas
                        </span>
                        <span className="font-heading font-extrabold text-lg text-red-700 numeric">
                          {totals.units_canceled || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="border border-zinc-200 rounded-md overflow-hidden">
              <div className="max-h-[55vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase text-zinc-500 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Folio</th>
                      <th className="text-left px-3 py-2">Fecha</th>
                      <th className="text-left px-3 py-2">Cliente</th>
                      <th className="text-left px-3 py-2">Vendedor</th>
                      <th className="text-right px-3 py-2">Cant.</th>
                      <th className="text-right px-3 py-2">Precio unit.</th>
                      <th className="text-right px-3 py-2">Subtotal</th>
                      <th className="text-left px-3 py-2">Estado</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="history-rows">
                    {loading && (
                      <tr><td colSpan={9} className="px-3 py-12 text-center text-zinc-400">Cargando…</td></tr>
                    )}
                    {!loading && rows.length === 0 && (
                      <tr><td colSpan={9} className="px-3 py-12 text-center text-zinc-400 dotted-bg">Este producto aún no aparece en ninguna nota de venta.</td></tr>
                    )}
                    {!loading && rows.map((r) => (
                      <tr key={r.sale_id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`history-row-${r.folio}`}>
                        <td className="px-3 py-2 font-mono-data font-semibold text-[#002FA7]">#{r.folio}</td>
                        <td className="px-3 py-2 text-zinc-700">{r.invoice_date || "—"}</td>
                        <td className="px-3 py-2 text-zinc-900">{r.client_name || <span className="text-zinc-400 italic">Sin cliente</span>}</td>
                        <td className="px-3 py-2 text-zinc-600 text-xs">{r.seller_name || "—"}</td>
                        <td className="px-3 py-2 text-right numeric font-semibold">{r.qty}</td>
                        <td className="px-3 py-2 text-right numeric text-zinc-700">{fmt(r.unit_price, r.currency)}</td>
                        <td className="px-3 py-2 text-right numeric font-semibold">{fmt(r.subtotal, r.currency)}</td>
                        <td className="px-3 py-2">{statusBadge(r.status, r)}</td>
                        <td className="px-2 py-2 text-right">
                          <a
                            href={`/sales?focus=${r.sale_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#002FA7]/10 text-[#002FA7]"
                            title="Abrir nota"
                            data-testid={`open-sale-${r.folio}`}
                          >
                            <ArrowRight size={14} weight="bold" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryStat({ label, value, highlight, sub }) {
  return (
    <div className={`p-3 rounded-md border ${highlight ? "border-[#002FA7]/30 bg-[#002FA7]/5" : "border-zinc-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-extrabold tracking-tight ${highlight ? "text-[#002FA7]" : "text-zinc-900"} numeric`}>
        {typeof value === "number" ? value.toLocaleString("es-MX") : value}
      </div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5 leading-tight">{sub}</div>}
    </div>
  );
}

function TierStat({ label, usd, mxn, fmt, highlight, testid }) {
  // Only render currencies that actually contribute; if both are zero we still
  // show "MX$0.00" so the tile isn't empty.
  const hasUsd = usd > 0;
  const hasMxn = mxn > 0 || !hasUsd;
  return (
    <div
      className={`p-2.5 rounded-md border bg-white ${highlight ? "border-[#002FA7]/40 bg-[#002FA7]/5" : "border-zinc-200"}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono font-semibold">{label}</div>
      <div className="mt-1 space-y-0.5">
        {hasMxn && (
          <div className={`font-heading font-extrabold tracking-tight numeric ${highlight ? "text-[#002FA7] text-base" : "text-zinc-900 text-sm"}`}>
            {fmt(mxn, "MXN")}
          </div>
        )}
        {hasUsd && (
          <div className={`font-heading font-extrabold tracking-tight numeric ${highlight ? "text-[#002FA7] text-base" : "text-zinc-900 text-sm"}`}>
            {fmt(usd, "USD")}
          </div>
        )}
      </div>
    </div>
  );
}


